// From-scratch client router. No library. Handles route matching, param
// extraction, per-route <head> metadata (title/meta/canonical/og/JSON-LD),
// an explicit not-found state with noindex set-and-cleared, and a
// renderShell() fast-paint pattern for routes that have static markup
// independent of their data fetch.

const APP_ROOT_ID = 'app';
const SITE_ORIGIN = 'https://jwk.dk';

let currentRoutes = [];
let currentNotFoundRoute = null;

export function initRouter(routes, notFoundRoute) {
  currentRoutes = routes;
  currentNotFoundRoute = notFoundRoute;

  // Intercept clicks on any [data-link] anchor: prevent the full page
  // load, push the new URL, and re-render client-side instead.
  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('[data-link]');
    if (!anchor) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const url = new URL(anchor.href, location.origin);
    if (url.origin !== location.origin) return;

    event.preventDefault();
    if (url.pathname === location.pathname && url.search === location.search) return;

    history.pushState({}, '', url.pathname + url.search);
    loadRoute();
  });

  window.addEventListener('popstate', loadRoute);

  return loadRoute();
}

// Converts a route pattern like '/projects/:slug' into a regex and
// extracts named params from an actual path. Returns null on no match.
export function matchPath(routePath, actualPath) {
  const paramNames = [];
  const pattern = routePath
    .replace(/\/+$/, '') // no trailing slash in the pattern itself
    .split('/')
    .map((segment) => {
      if (segment.startsWith(':')) {
        paramNames.push(segment.slice(1));
        return '([^/]+)';
      }
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');

  const regex = new RegExp(`^${pattern || '/'}$`);
  const match = regex.exec(actualPath);
  if (!match) return null;

  const params = {};
  paramNames.forEach((name, i) => {
    params[name] = decodeURIComponent(match[i + 1]);
  });
  return params;
}

// Strips trailing slashes and any query/hash — must match whatever
// server-rendering/prerendering layer, if any, computes for the same URL.
export function canonicalPath(pathname) {
  if (pathname.length > 1) {
    pathname = pathname.replace(/\/+$/, '');
  }
  return pathname || '/';
}

export async function loadRoute() {
  const path = canonicalPath(location.pathname);

  let matchedRoute = null;
  let params = {};

  for (const route of currentRoutes) {
    const result = matchPath(route.path, path);
    if (result) {
      matchedRoute = route;
      params = result;
      break;
    }
  }

  const app = document.getElementById(APP_ROOT_ID);

  if (!matchedRoute) {
    setNoindex(true);
    applyRouteMeta(currentNotFoundRoute, path, params);
    const mod = await currentNotFoundRoute.view();
    const render = mod.default || mod[Object.keys(mod)[0]];
    app.innerHTML = await render(params);
    finishRender();
    return;
  }

  // Every successful navigation to a real route clears any noindex left
  // behind by a previous not-found render — it's a single-page app, so
  // that tag would otherwise leak onto the next real page.
  setNoindex(false);
  applyRouteMeta(matchedRoute, path, params);

  const mod = await matchedRoute.view();
  const render = mod.default;

  // If the view also exports renderShell() — pure static markup that
  // doesn't depend on any data fetch — paint that immediately, then
  // replace it once the real content resolves.
  if (typeof mod.renderShell === 'function') {
    app.innerHTML = mod.renderShell(params);
  }

  const html = await render(params);
  app.innerHTML = html;
  finishRender();
}

function finishRender() {
  // Per-page init that must re-run after every route render, since the
  // DOM under #app is fully replaced each time.
  window.dispatchEvent(new CustomEvent('route:rendered'));

  const navCheckbox = document.getElementById('nav-menu-checkbox');
  if (navCheckbox) navCheckbox.checked = false;

  window.scrollTo(0, 0);
}

function applyRouteMeta(route, path, params) {
  setTitle(typeof route.title === 'function' ? route.title(params) : route.title);
  setMetaDescription(
    typeof route.description === 'function' ? route.description(params) : route.description
  );
  setCanonical(SITE_ORIGIN + path);
  setOgTags({
    title: typeof route.title === 'function' ? route.title(params) : route.title,
    description:
      typeof route.description === 'function' ? route.description(params) : route.description,
    url: SITE_ORIGIN + path,
  });
  setJsonLd(buildBreadcrumbList(route, params));
}

export function setTitle(title) {
  document.title = title || 'jwk.dk';
}

export function setMetaDescription(description) {
  setMetaTag('name', 'description', description || '');
}

export function setCanonical(url) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = url;
}

export function setOgTags({ title, description, url }) {
  setMetaTag('property', 'og:title', title || '');
  setMetaTag('property', 'og:description', description || '');
  setMetaTag('property', 'og:url', url || '');
  setMetaTag('property', 'og:type', 'website');
}

function setMetaTag(attr, value, content) {
  let tag = document.querySelector(`meta[${attr}="${value}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setNoindex(noindex) {
  setMetaTag('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');
}

// Injects/removes a <script type="application/ld+json"> block per route,
// built from a small `breadcrumbs` array on the route config.
function setJsonLd(data) {
  let script = document.getElementById('route-json-ld');
  if (!data) {
    if (script) script.remove();
    return;
  }
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'route-json-ld';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function buildBreadcrumbList(route, params) {
  const breadcrumbs =
    typeof route.breadcrumbs === 'function' ? route.breadcrumbs(params) : route.breadcrumbs;
  if (!breadcrumbs || !breadcrumbs.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: SITE_ORIGIN + crumb.path,
    })),
  };
}
